import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useData } from '../../contexts/DataContext';
import { usePreferences } from '../../contexts/PreferencesContext';
import { EndeavorApi, EndeavorTypeApi, EndeavorMetadataApi, EventTypeApi } from '../../services/ApiService';
import { OrgAdminApi } from '../../services/api/OrgAdminApi';
import { DocumentEndeavorApi } from '../../services/api/DocumentEndeavorAPI';
import SyllabusStep from './steps/SyllabusStep';
import CourseReviewSection from './steps/CourseReviewSection';
import { normalizeSyllabusResponse } from '../../services/ai/syllabusNormalizer';
import { UsageEventsTracker } from '../../services/UsageEventsTracker';
import './CreateCourseWizard.css';

// Sanitize malformed datetime strings like 2025-11-03T09:00:00+00:00T09:00:00
const sanitizeDateTime = (val) => {
  if (!val || typeof val !== 'string') return val;
  let s = val.trim();
  // If duplicate 'T' after timezone offset, keep up to first offset
  if ((s.match(/T/g) || []).length > 1) {
    // Strategy: split at 'T', rebuild until a timezone offset encountered, drop trailing orphan segments
    const parts = s.split('T');
    if (parts.length >= 3) {
      // Reconstruct first two parts; if second already contains offset (+/- or Z) then discard rest
      const second = parts[1];
      if (/[+-]\d{2}:?\d{2}|Z/.test(second)) {
        s = parts[0] + 'T' + second; // drop extras
      } else {
        s = parts.slice(0,2).join('T');
      }
    }
    s = s.replace(/T{2,}/g,'T');
  }
  // Remove any invalid trailing characters
  s = s.replace(/[^0-9TZ:+-].*$/, '');
  // If still bad and only date provided
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s + 'T00:00:00';
  return s;
};

// Convert a datetime string to naive (strip timezone/Z and ensure seconds) for backend LocalDateTime
const toNaiveDateTime = (val) => {
  if (!val) return val;
  let s = String(val).trim();
  // First sanitize duplicates etc.
  s = sanitizeDateTime(s) || s;
  // Remove trailing timezone offset or Z
  s = s.replace(/([+-]\d{2}:?\d{2}|Z)$/,'');
  // If only date provided
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s + 'T23:59:59';
  // If missing seconds (YYYY-MM-DDTHH:mm)
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(s)) return s + ':00';
  // If has seconds already, keep as-is (after stripping tz)
  return s;
};

// Map metadata definition names (lowercased) to their IDs for a given endeavor type
const fetchMetadataDefinitionMap = async (endeavorTypeId) => {
  try {
    const resp = await EndeavorTypeApi.getEndeavorTypeMetadataDefinitions(endeavorTypeId);
    if (!resp.ok) return {};
    const defs = await resp.json();
    const map = {};
    defs.forEach(entry => {
      const name = entry?.metadataDefinition?.name || entry?.name;
      const id = entry?.metadataDefinitionId || entry?.id;
      if (name && id) {
        map[name.trim().toLowerCase()] = id;
      }
    });
    return map;
  } catch (e) {
    console.warn('Failed to fetch metadata definitions', e);
    return {};
  }
};

// Save course-related metadata values (skip empty values)
const saveCourseMetadata = async (endeavorId, endeavorTypeId, courseData) => {
  const syllabusInfo = courseData.syllabusPreview?.syllabus_data?.course_information || {};
  const defMap = await fetchMetadataDefinitionMap(endeavorTypeId);

  const values = {
    'course name': courseData.courseName || syllabusInfo.course_name,
    'course code': courseData.courseCode || syllabusInfo.course_code || '',
    'professor': courseData.instructor || syllabusInfo.instructor,
    'start date': courseData.startDate,
    'end date': courseData.endDate,
  'description': courseData.description || '',
  'required materials': courseData.requiredMaterials || '',
    'office hours': syllabusInfo.office_hours,
    'course website': syllabusInfo.course_website,
    'course location': courseData.location || '',
    'semester': courseData.semester || '',
    'meeting times': courseData.meetingTimes || syllabusInfo.meeting_time,
    'meeting start time': courseData.meetingStartTime || '',
    'meeting end time': courseData.meetingEndTime || '',
    'schedule pattern': courseData.schedulePattern || '',
    'instructor emails': Array.isArray(syllabusInfo.emails) ? syllabusInfo.emails.join(', ') : '',
    'instructor phone': syllabusInfo.phone || ''
  };

  const ops = Object.entries(values)
    .filter(([, v]) => v !== undefined && v !== null && String(v).trim() !== '')
    .map(async ([key, val]) => {
      const defId = defMap[key];
      if (!defId) { console.log(`No definition ID for metadata "${key}"`); return null; }
      try {
        const resp = await EndeavorMetadataApi.addMetadata({
          endeavorId,
          metadataDefinitionId: defId,
          value: String(val)
        });
        if (!resp.ok) {
          console.warn(`Failed to save metadata "${key}"`, await resp.text().catch(()=>'')); 
        }
        return resp.ok;
      } catch (e) {
        console.warn(`Error saving metadata "${key}":`, e);
        return false;
      }
    });

  if (ops.length) {
    const results = await Promise.all(ops);
    const success = results.filter(Boolean).length;
    console.log(`Saved ${success}/${ops.length} course metadata items.`);
  }
};

function CreateCourseWizard({
  orgId = null,
  embedded = false,
  onCreated = null,
  onCancel = null,
} = {}) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { patchPreferences } = usePreferences();
  const { clearCache, hydrateDashboard, createCourseDraft, setCreateCourseDraft } = useData();

  // ✅ Ensure both loading (final create) and processing (intermediate step work) states exist
  const [courseData, setCourseData] = useState({
    // Basic course info
    courseName: '',
    instructor: '',
    startDate: '',
    endDate: '',
    location: '',
    description: '',
    // NEW fields
    semester: '',
    meetingTimes: '',            // legacy (kept for fallback display)
    meetingStartTime: '',        // HH:mm
    meetingEndTime: '',          // HH:mm
  requiredMaterials: '',
    schedulePattern: '',
    // Syllabus data
    syllabusDocumentId: null,
    selectedSyllabusDocument: null,
    syllabusFile: null,
    
    // Processing results
    syllabusPreview: null,
    processingError: null,
    
    // Created endeavor
    endeavorId: null
  });
  const [events] = useState([]);
  const [parentEvent, setParentEvent] = useState(null);
  const [normalized, setNormalized] = useState(null);
  const [manualMode, setManualMode] = useState(false);

  const [loading, setLoading] = useState(false);      // final creation spinner
  const [makeShared, setMakeShared] = useState(false);  // shared course toggle
  const [processing] = useState(false); // step-level processing (if used by steps)
  const [creationPhrases] = useState([
    'Summoning hamsters to power the servers…',
    'Negotiating with scholarly squirrels…',
    'Indexing lecture notes on ancient parchment…',
    'Calibrating classroom coffee levels…',
    'Scheduling snack breaks for peak focus…',
    'Organizing assignments by difficulty curve…'
  ]);
  const [creationPhraseIdx, setCreationPhraseIdx] = useState(() => Math.floor(Math.random() * creationPhrases.length));
  useEffect(() => {
    if (!loading) return;
    const id = setInterval(() => {
      setCreationPhraseIdx(i => (i + (Math.floor(Math.random() * 5) + 1)) % creationPhrases.length);
    }, 4000);
    return () => clearInterval(id);
  }, [loading, creationPhrases.length]);

  // Auto-populate draft events/assignments when syllabus processing yields normalized data
  const [hasAutoProcessed, setHasAutoProcessed] = useState(false);
  useEffect(() => {
    if (normalized && !hasAutoProcessed) {
      setHasAutoProcessed(true);
      processSyllabus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [normalized, hasAutoProcessed]);

  // When syllabusPreview becomes available, normalize and prefill fields
  useEffect(() => {
    const preview = courseData?.syllabusPreview;
    if (!preview) { setNormalized(null); return; }
    try {
      const n = normalizeSyllabusResponse(preview);
      setNormalized(n);
      setCourseData(prev => ({
        ...prev,
        courseName: prev.courseName || n.course.courseName,
        semester: prev.semester || n.course.semester,
        location: prev.location || n.course.location,
        startDate: prev.startDate || n.course.startDate,
        endDate: prev.endDate || n.course.endDate,
        meetingTimes: prev.meetingTimes || n.course.meetingTimeLabel,
        meetingStartTime: prev.meetingStartTime || n.course.meetingStartTime,
        meetingEndTime: prev.meetingEndTime || n.course.meetingEndTime,
  schedulePattern: prev.schedulePattern || n.course.rrule
      }));
    } catch (e) {
      console.warn('normalizeSyllabusResponse failed:', e);
    }
  }, [courseData?.syllabusPreview, setCourseData]);

  // Update processSyllabus to create preview events/assignments in wizard state
  // Helpers to ensure stable ids for draft items
  const ensureId = (item) => {
    if (!item || typeof item !== 'object') return item;
    if (item.id) return item;
    const gen = (typeof window !== 'undefined' && window.crypto && window.crypto.randomUUID) ? window.crypto.randomUUID() : null;
    const id = gen || `tmp_${Date.now()}_${Math.floor(Math.random()*100000)}`;
    return { ...item, id };
  };

  const normalizeEvents = (events = []) => (events || []).map(e => {
    const ensured = ensureId(e);
    const sourceId = e.eventId || e.id || e.event_id || null;
    return { ...ensured, sourceId };
  });
  // Convert various date/datetime inputs into a stable YYYY-MM-DD string (local date)
  const toYMD = (d) => {
    if (!d) return '';
    if (typeof d === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(d)) return d;
    try {
      const dt = new Date(d);
      if (Number.isNaN(dt.getTime())) return '';
      const yyyy = dt.getFullYear();
      const mm = String(dt.getMonth() + 1).padStart(2, '0');
      const dd = String(dt.getDate()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd}`;
    } catch (e) {
      return '';
    }
  };

  const normalizeAssignments = (assignments = []) => (assignments || []).map(a => {
    const ensured = ensureId(a);
    // Map possible source fields into canonical dueDate (YYYY-MM-DD) while avoiding timezone shifts
    const src = a.dueDate || a.dueDatetime || a.date || a.due_at || a.start || a.assignedDate || null;
    const dueDate = toYMD(src);
    // preserve original source id for reconciliation
    const sourceId = a.assignmentId || a.id || a.assignment_id || null;
    return { ...ensured, sourceId, dueDate };
  });

  const processSyllabus = async () => {
    if (!normalized) return;
    try {
      // Build session events
      const sessionEvents = normalized.sessions.map(s => ({
        sourceId: s.id || s.eventId || s.event_id || null,
        title: s.title || 'Class Session',
        description: s.description || '',
        date: s.date,
        startTime: s.startTime || courseData.meetingStartTime || '00:00',
        endTime: s.endTime || courseData.meetingEndTime || '00:00',
        location: normalized.course.location || '',
        eventType: s.canceled ? 'holiday' : 'class_session',
        isRecurring: false,
        sequence: s.sequence,
        isFromSyllabus: true,
        topics: s.topics || [],
        readings: s.readings || [],
        canceled: Boolean(s.canceled),
      }));

      setParentEvent(null);
      // Persist parsed session events into DataContext canonical draft
      setCreateCourseDraft(prev => {
        const prevEvents = (prev.draftEvents || []);
        const removed = new Set(prev.removedSyllabusSourceIds || []);
        const prevBySource = new Map(prevEvents.map(e => [e.sourceId || e.id, e]));
        const normalizedEvents = normalizeEvents(sessionEvents)
          .filter(ne => !removed.has(ne.sourceId || ne.id))
          .map(ne => {
            const existing = prevBySource.get(ne.sourceId || ne.id);
            if (existing) return { ...ne, ...existing, isFromSyllabus: true, id: existing.id };
            return ne;
          });
        return {
          ...prev,
          draftEvents: [
            ...prevEvents.filter(e => !e.isFromSyllabus),
            ...normalizedEvents
          ]
        };
      });

      // Map assignments; event linking deferred until course creation returns event IDs
      const mappedAssignments = normalized.assignments.map(a => ({
        sourceId: a.id || a.assignmentId || a.assignment_id || null,
        title: a.title,
        description: a.description || '',
        assignmentType: a.assignmentType || 'assignment',
        // Normalize to naive without timezone to avoid backend parse error
        dueDatetime: a.dueDatetime ? toNaiveDateTime(a.dueDatetime) : null,
        points: a.points ?? null,
        priority: (a.weightPercent && a.weightPercent >= 15) ? 'high' : 'medium',
        weightPercent: a.weightPercent ?? null,
        groupWork: Boolean(a.groupWork),
        dueInstructions: a.dueInstructions || '',
        isFromSyllabus: true,
        sessionSequence: a.sessionSequence ?? null,
        eventId: null
      }));

      // Convert readings for each session into reading-type assignments (preview only)
      const readingAssignments = normalized.sessions.flatMap(s => {
        if (!s.readings || !s.readings.length) return [];
        return s.readings.map((r, idx) => ({
          sourceId: `reading-${s.sequence || 'x'}-${idx}`,
          title: typeof r === 'string' ? r.slice(0,140) : (r.title || 'Reading'),
          description: typeof r === 'string' ? r : (r.description || r.title || ''),
          assignmentType: 'reading',
          dueDatetime: s.date ? toNaiveDateTime(`${s.date}T08:00:00`) : null, // morning of session
          points: null,
          priority: 'LOW',
          weightPercent: null,
          groupWork: false,
          dueInstructions: '',
          isFromSyllabus: true,
          sessionSequence: s.sequence != null ? s.sequence : null,
          eventId: null
        }));
      });

      // Persist parsed assignments into DataContext canonical draft
      setCreateCourseDraft(prev => {
        const prevAssignments = (prev.draftAssignments || []);
        const removed = new Set(prev.removedSyllabusSourceIds || []);
        const prevBySource = new Map(prevAssignments.map(a => [a.sourceId || a.id, a]));
        const normMapped = normalizeAssignments(mappedAssignments)
          .filter(na => !removed.has(na.sourceId || na.id))
          .map(na => {
            const existing = prevBySource.get(na.sourceId || na.id);
            if (existing) return { ...na, ...existing, isFromSyllabus: true, id: existing.id };
            return na;
          });
        const normReadings = normalizeAssignments(readingAssignments)
          .filter(na => !removed.has(na.sourceId || na.id))
          .map(na => {
            const existing = prevBySource.get(na.sourceId || na.id);
            if (existing) return { ...na, ...existing, isFromSyllabus: true, id: existing.id };
            return na;
          });
        return {
          ...prev,
          draftAssignments: [
            ...prevAssignments.filter(x => !x.isFromSyllabus),
            ...normMapped,
            ...normReadings
          ]
        };
      });
    } catch (e) {
      console.error('processSyllabus mapping failed:', e);
    }
  };

  // Create the course with all events and assignments
  const createCourse = async () => {
    try {
      setLoading(true);
      console.log('Creating course...');

      const corrId = UsageEventsTracker.startCorrelation('course_create');
      UsageEventsTracker.recordUserAction('COURSE_CREATION_STARTED', {
        action: 'start',
        category: 'course',
        label: 'create_course',
        metadata: {
          courseName: courseData.courseName,
          source: 'create_course_wizard'
        }
      }, { correlationId: corrId });

      // 1) Resolve Course endeavor type
      const typesResponse = await EndeavorTypeApi.getEndeavorTypes();
      if (!typesResponse.ok) {
        const msg = await typesResponse.text().catch(()=> 'Failed to load endeavor types');
        throw new Error(msg);
      }
      const types = await typesResponse.json();
      const courseType = types.find(type => 
        type.name?.toLowerCase() === 'course' || 
        type.typeName?.toLowerCase() === 'course'
      );

      if (!courseType) {
        throw new Error('Course endeavor type not found');
      }

      const endeavorTypeId = courseType.id || courseType.endeavorTypeId;

      // 2) Create the endeavor
      const endeavorPayload = {
        name: courseData.courseName || 'New Course',
        description: courseData.description || `Course: ${courseData.courseName || ''}`,
        startDate: courseData.startDate || null,
        endDate: courseData.endDate || null,
        endeavorTypeId,
        userId: user.user_id || user.userId || user.id,
        location: courseData.location || ''
      };
      const createEndeavorResp = orgId
        ? await OrgAdminApi.createOrgOwnedCourse(orgId, endeavorPayload)
        : makeShared
          ? await EndeavorApi.createSharedEndeavor(endeavorPayload)
          : await EndeavorApi.createEndeavor(endeavorPayload);
      if (!createEndeavorResp.ok) {
        const msg = await createEndeavorResp.text().catch(()=> 'Failed to create endeavor');
        throw new Error(msg);
      }
      const createdEndeavor = await createEndeavorResp.json();
      const newEndeavorId = createdEndeavor.endeavorId || createdEndeavor.id;
      if (!newEndeavorId) throw new Error('Endeavor created, but no ID returned');

      // 2b) If a syllabus document was used, associate it with the newly created endeavor
      try {
        if (courseData.syllabusDocumentId) {
          console.log('[CreateCourseWizard] Associating syllabus document with new course endeavor', courseData.syllabusDocumentId, newEndeavorId);
          const assocResp = await DocumentEndeavorApi.associateDocumentWithEndeavor(courseData.syllabusDocumentId, newEndeavorId);
          if (!assocResp.ok) {
            console.warn('Syllabus document association failed:', assocResp.status, await assocResp.text().catch(()=>''));
          } else {
            console.log('Syllabus document successfully associated with endeavor');
          }
        } else {
          console.log('[CreateCourseWizard] No syllabusDocumentId present; skipping association');
        }
      } catch (assocErr) {
        console.warn('Error associating syllabus document with endeavor:', assocErr);
      }

      // 3) Create events (sessions and any manual events)
      const toIso = (date, time) => {
        if (!date) return null;
        // Pad time: '7:00' → '07:00', '9:30' → '09:30'
        let t = time || '';
        if (/^\d:\d{2}$/.test(t)) t = '0' + t;
        const hhmm = (t && t.length === 5) ? `${t}:00` : (t || '00:00:00');
        return sanitizeDateTime(`${date}T${hhmm}`);
      };

      // Prefer normalized sessions if available; else use events state
      // Fetch event types once so we can map friendly name (e.g., class_session) -> eventTypeId
      let eventTypes = [];
      try {
        const etResp = await EventTypeApi.getEventTypes();
        eventTypes = Array.isArray(etResp) ? etResp : (etResp && etResp.json ? await etResp.json() : []);
      } catch (e) { console.warn('Could not load event types – proceeding without IDs'); }
      const eventTypeNameToId = new Map();
      eventTypes.forEach(t => {
        const name = (t.name || t.typeName || t.eventTypeName || '').toLowerCase();
        const id = t.eventTypeId || t.id;
        if (name && id) eventTypeNameToId.set(name, id);
      });

      const coerceNaive = (dt) => {
        if (!dt) return dt;
        // Remove timezone offset or Z to produce naive local datetime string expected by backend
        return String(dt).replace(/([+-]\d{2}:?\d{2}|Z)$/,'');
      };

      const buildEnrichedDescription = (base, topics = [], readings = []) => {
        const lines = [];
        if (base && base.trim()) lines.push(base.trim());
        if (topics.length) {
          lines.push('\nTopics:\n' + topics.map(t => `• ${t}`).join('\n'));
        }
        if (readings.length) {
          lines.push('\nReadings:\n' + readings.map(r => `• ${r}`).join('\n'));
        }
        return lines.join('\n').trim();
      };

      const draftEvents = createCourseDraft?.draftEvents || [];
      const draftAssignments = createCourseDraft?.draftAssignments || [];

      // Build events entirely from the canonical wizard drafts (single source of truth).
      const draftEventsAll = draftEvents || [];
      const sessionSource = draftEventsAll.filter(e => e.isFromSyllabus).map(e => {
        const eventTypeName = e.eventType || (e.canceled ? 'holiday' : 'class_session');
        const topics = e.topics || [];
        const readings = e.readings || [];
        return {
          sourceId: e.sourceId || e.eventId || e.id || null,
          name: e.title || e.name || 'Class Session',
          description: buildEnrichedDescription(e.description || '', topics, readings),
          startDatetime: coerceNaive(toIso(e.date || e.start || e.startDatetime, e.startTime || e.start)),
          endDatetime: coerceNaive(toIso(e.date || e.end || e.endDatetime, e.endTime || e.end)),
          eventTypeName,
          eventTypeId: eventTypeNameToId.get(eventTypeName) || undefined,
          location: e.location || courseData.location || '',
          sequence: typeof e.sequence === 'number' ? e.sequence : null,
          canceled: Boolean(e.canceled),
          topics,
          readings
        };
      });

      

      // Include any draft events that are not from syllabus (user-added or edited)
      const manualDrafts = draftEventsAll
        .filter(e => !e.isFromSyllabus)
        .map(e => {
          const eventTypeName = e.eventType || 'class_session';
          const topics = e.topics || [];
          const readings = e.readings || [];
          return {
            sourceId: e.sourceId || e.eventId || e.id || null,
            name: e.title || e.name || 'Event',
            description: buildEnrichedDescription(e.description || '', topics, readings),
            startDatetime: coerceNaive(toIso(e.date, e.startTime)),
            endDatetime: coerceNaive(toIso(e.date, e.endTime)),
            eventTypeName,
            eventTypeId: eventTypeNameToId.get(eventTypeName) || undefined,
            location: e.location || courseData.location || '',
            sequence: null,
            canceled: false,
            topics,
            readings
          };
        });

      // All source events for create come from drafts
      const sourceEvents = [...sessionSource, ...manualDrafts];

    console.log('[CreateCourse] FINAL CREATE payload counts', sourceEvents.length, draftAssignments.length);
    if (draftAssignments.length) console.log('[CreateCourse] sample final assignment', draftAssignments[0]);

      // Build a lookup of all assignments grouped by sessionSequence
      const assignmentsBySeq = (draftAssignments?.length ? draftAssignments : []).reduce((acc, a) => {
        const seq = a.sessionSequence != null ? Number(a.sessionSequence) : null;
        if (seq != null) {
          if (!acc[seq]) acc[seq] = [];
          acc[seq].push(a);
        }
        return acc;
      }, {});

      // Helper to normalize due datetime & sanitize
      const normalizeDue = (val, fallbackDate) => {
        if (!val) {
          if (!fallbackDate) return null;
          return toNaiveDateTime(`${fallbackDate}T23:59:59`);
        }
        if (typeof val === 'string') {
          let v = val;
          if (/^\d{4}-\d{2}-\d{2}$/.test(v)) v = `${v}T23:59:59`;
          return toNaiveDateTime(v);
        }
        return val;
      };

      // ── Build bulk payload ──────────────────────────────────────────
      // Map each source event's sequence → its index in bulkEvents so assignments
      // can reference the correct tempEventIndex.
      const sequenceToIndex = new Map();
      const bulkEvents = sourceEvents.map((ev, idx) => {
        if (ev.sequence != null) sequenceToIndex.set(Number(ev.sequence), idx);

        let startIso = null;
        let endIso = null;
        const startRaw = ev.startDatetime || ev.start || null;
        const endRaw = ev.endDatetime || ev.end || null;
        if (startRaw && String(startRaw).includes('T')) startIso = coerceNaive(sanitizeDateTime(startRaw));
        else startIso = coerceNaive(toIso(ev.date || startRaw, ev.startTime || startRaw));
        if (endRaw && String(endRaw).includes('T')) endIso = coerceNaive(sanitizeDateTime(endRaw));
        else endIso = coerceNaive(toIso(ev.date || endRaw, ev.endTime || endRaw || ev.startTime));

        return {
          name: ev.name,
          description: ev.description || '',
          startDatetime: startIso,
          endDatetime: endIso,
          location: ev.location || null,
          eventTypeId: ev.eventTypeId || null,
          sourceId: ev.sourceId || null,
        };
      });

      // Build bulk assignments — session-linked assignments get a tempEventIndex,
      // standalone assignments get null.
      const bulkAssignments = [];

      // Session-linked assignments (ordered by event they belong to)
      sourceEvents.forEach((ev, evIdx) => {
        const seq = ev.sequence != null ? Number(ev.sequence) : null;
        const sessionAssignments = seq != null ? (assignmentsBySeq[seq] || []) : [];
        for (const a of sessionAssignments) {
          const fallbackDate = ev.startDatetime ? String(ev.startDatetime).slice(0, 10)
            : (ev.date || courseData.endDate || new Date().toISOString().slice(0, 10));
          bulkAssignments.push({
            title: a.title || 'Untitled',
            description: a.description || '',
            dueDatetime: normalizeDue(a.dueDatetime || a.dueDate, fallbackDate),
            assignmentType: a.assignmentType || 'assignment',
            priority: typeof a.priority === 'number' ? a.priority : null,
            weightPercent: a.weightPercent ?? null,
            groupWork: Boolean(a.groupWork),
            source: 'SYLLABUS_SESSION',
            sourceId: a.sourceId || a.assignmentId || a.id || null,
            tempEventIndex: evIdx,
          });
        }
      });

      // Standalone assignments (no sessionSequence)
      const standaloneAssignments = (draftAssignments || []).filter(a => a.sessionSequence == null);
      for (const a of standaloneAssignments) {
        const fallbackDate = courseData.endDate || new Date().toISOString().slice(0, 10);
        bulkAssignments.push({
          title: a.title || 'Untitled',
          description: a.description || '',
          dueDatetime: normalizeDue(a.dueDatetime || a.dueDate, fallbackDate),
          assignmentType: a.assignmentType || 'assignment',
          priority: typeof a.priority === 'number' ? a.priority : null,
          weightPercent: a.weightPercent ?? null,
          groupWork: Boolean(a.groupWork),
          source: 'SYLLABUS_STANDALONE',
          sourceId: a.sourceId || a.assignmentId || a.id || null,
          tempEventIndex: null,
        });
      }

      // Single bulk request creates all events + assignments
      if (bulkEvents.length > 0 || bulkAssignments.length > 0) {
        const bulkResp = await EndeavorApi.bulkCreateCourseContent(newEndeavorId, {
          events: bulkEvents,
          assignments: bulkAssignments,
        });
        if (!bulkResp.ok) {
          console.warn('[CreateCourse] Bulk content create failed:', await bulkResp.text().catch(() => ''));
        } else {
          const bulkResult = await bulkResp.json();
          console.log('[CreateCourse] ✅ Bulk content created:', bulkResult.eventsCreated, 'events,', bulkResult.assignmentsCreated, 'assignments');
        }
      }

      // 5) Save course metadata
      await saveCourseMetadata(newEndeavorId, endeavorTypeId, courseData);

      // Clear wizard drafts now that course was created
      try {
        setCreateCourseDraft({ draftEvents: [], draftAssignments: [] });
      } catch (e) { /* ignore */ }

      // 6) Clear caches and refresh dashboard after entire course creation process
      console.log('[CreateCourseWizard] Course creation complete, invalidating caches...');
      try {
        // Clear aggregated caches to force dashboard refresh (dashboard_full_hydration auto-resets hydration flag)
        const userId = user?.id || user?.userId;
        if (userId) {
          clearCache(`endeavors_user-${userId}`);
        }
        clearCache('user-events');
        clearCache('user-assignments');
        clearCache('dashboard_full_hydration'); // This automatically resets dashboardHydratedRef
        console.log('[CreateCourseWizard] ✅ Cache invalidation complete');
      } catch (e) {
        console.warn('[CreateCourseWizard] Cache clear failed:', e);
      }

      // Refresh dashboard data before navigating so the new course appears immediately
      try {
        if (hydrateDashboard && typeof hydrateDashboard === 'function') {
          console.log('[CreateCourseWizard] Refreshing dashboard data...');
          await hydrateDashboard(user?.id || user?.userId);
          console.log('[CreateCourseWizard] ✅ Dashboard refresh complete');
        }
      } catch (e) {
        console.warn('[CreateCourseWizard] hydrateDashboard failed:', e);
      }

      // Mark that the user now has courses so routing defaults to dashboard
      try { patchPreferences({ has_courses: true }); } catch (_) {}

      // When embedded (modal/inline), let the host decide what to do next.
      if (embedded || typeof onCreated === 'function') {
        try {
          UsageEventsTracker.recordUserAction('COURSE_CREATION_SUCCESS', {
            action: 'complete',
            category: 'course',
            entityType: 'endeavor',
            entityId: newEndeavorId,
            label: 'create_course',
            metadata: {
              courseName: courseData.courseName,
              endeavorId: newEndeavorId,
              source: orgId ? 'create_course_wizard_org' : 'create_course_wizard'
            }
          }, { correlationId: UsageEventsTracker.getCorrelation('course_create') });
          UsageEventsTracker.clearCorrelation('course_create');
        } catch (_) {}
        if (typeof onCreated === 'function') {
          try { onCreated({ endeavorId: newEndeavorId, endeavor: createdEndeavor }); } catch (_) {}
        }
        return;
      }

      // Navigate to the dashboard (or fallback to direct course view).
      try {
        navigate('/dashboard', { state: { highlightCourseId: newEndeavorId } });
        UsageEventsTracker.recordUserAction('COURSE_CREATION_SUCCESS', {
          action: 'complete',
          category: 'course',
          entityType: 'endeavor',
          entityId: newEndeavorId,
          label: 'create_course',
          metadata: {
            courseName: courseData.courseName,
            endeavorId: newEndeavorId,
            source: 'create_course_wizard'
          }
        }, { correlationId: UsageEventsTracker.getCorrelation('course_create') });
        UsageEventsTracker.clearCorrelation('course_create');
        setTimeout(() => {
          try { window.dispatchEvent(new CustomEvent('scoutiq:selectEndeavor', { detail: { id: newEndeavorId } })); } catch (e) {}
        }, 120);
      } catch (e) {
        try { navigate(`/course/${newEndeavorId}`); } catch (e) { /* ignore */ }
      }
    } catch (error) {
      console.error('Create course failed:', error);
      UsageEventsTracker.recordUserAction('COURSE_CREATION_FAILED', {
        action: 'error',
        category: 'course',
        label: 'create_course',
        metadata: {
          errorMessage: error.message,
          source: 'create_course_wizard'
        }
      }, { correlationId: UsageEventsTracker.getCorrelation('course_create') });
      UsageEventsTracker.clearCorrelation('course_create');
      alert(error.message || 'Failed to create course');
    } finally {
      setLoading(false);
    }
  };

  const canCreate = Boolean(courseData.courseName && courseData.startDate && courseData.endDate);

  const getQualityPercentage = () => courseData.syllabusPreview?.quality_assessment?.percentage
    ? Math.round(courseData.syllabusPreview.quality_assessment.percentage)
    : null;

 // Keep course start/end dates in sync with extracted/added events
 useEffect(() => {
   const allDates = [];
   if (parentEvent?.childEvents?.length) {
     parentEvent.childEvents.forEach(s => s?.startDate && allDates.push(s.startDate));
   }
  const draftEventsForDates = createCourseDraft?.draftEvents || [];
  draftEventsForDates.filter(e => !e.isParentEvent).forEach(e => {
    const d = e.startDate || e.date || e.start;
    if (d) allDates.push(d);
  });
   if (!allDates.length) return;
   const toDate = (d) => new Date(d);
   const fmt = (d) => new Date(d).toISOString().slice(0,10);
   const minD = allDates.reduce((a,b)=> toDate(a) < toDate(b) ? a : b);
   const maxD = allDates.reduce((a,b)=> toDate(a) > toDate(b) ? a : b);
   const nextStart = fmt(minD);
   const nextEnd = fmt(maxD);
   setCourseData(prev => {
     if (prev.startDate === nextStart && prev.endDate === nextEnd) return prev;
     return { ...prev, startDate: nextStart, endDate: nextEnd };
   });
}, [parentEvent, createCourseDraft?.draftEvents, setCourseData]);

  return (
    <div className="create-course-wizard single-page">
      {loading && (
        <div className="course-creation-overlay" role="alert" aria-live="polite">
          <div className="course-creation-panel">
            <div className="course-spinner" aria-hidden="true" />
            <p className="spinner-line">{creationPhrases[creationPhraseIdx]}</p>
            <p className="hint">Hang tight — creating your course, events, and assignments.</p>
          </div>
        </div>
      )}

      {/* ─── Simple Header ─── */}
      <header className="wizard-header">
        <div className="wizard-header-inner">
          <button
            type="button"
            className="wizard-back-link"
            onClick={() => {
              if (embedded && typeof onCancel === 'function') { onCancel(); return; }
              navigate(-1);
            }}
            aria-label={embedded ? 'Cancel' : 'Go back'}
          >
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M15 19l-7-7 7-7" strokeLinecap="round" strokeLinejoin="round"/></svg>
          </button>
          <h1 className="wizard-page-title">{orgId ? 'Create Org Course' : 'Create Course'}</h1>
          <div className="wizard-header-spacer" />
        </div>
      </header>

      {/* ─── Single-Page Content ─── */}
      <div className="wizard-content">
        {!manualMode && (
          <SyllabusStep
            courseData={courseData}
            setCourseData={setCourseData}
            processing={processing}
            parentEvent={parentEvent}
            events={events}
            onManualMode={() => setManualMode(true)}
          />
        )}

        {(manualMode || (courseData.syllabusPreview && !courseData.processingError)) && (
          <CourseReviewSection
            courseData={courseData}
            setCourseData={setCourseData}
            onCreateCourse={createCourse}
            loading={loading}
            canCreate={canCreate}
            qualityPercentage={getQualityPercentage()}
            startInEditMode={manualMode}
            isManualMode={manualMode}
            onSwitchToSyllabus={() => setManualMode(false)}
            makeShared={makeShared}
            setMakeShared={setMakeShared}
          />
        )}
      </div>
    </div>
  );
}

export default CreateCourseWizard;